import * as React from "react"

import { flexRender, type Header } from "@tanstack/react-table"
import { ArrowDown, ArrowUp, ChevronRight, MoreHorizontal } from "lucide-react"

import { cn } from "../../cn"
import { useCopyToClipboardFeedback } from "../../hooks/useCopyToClipboardFeedback"
import { Checkbox } from "../Checkbox/Checkbox"
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "../ContextMenu/ContextMenu"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../DropdownMenu/DropdownMenu"
import {
  normalizeResourceCopyTargets,
  ResourceMenuItems,
  type ResourceMenuAction,
} from "../ResourceMenu/ResourceMenu"
import { Tooltip } from "../Tooltip/Tooltip"
import {
  alignClasses,
  edgeFadeClasses,
  gutterCoverClasses,
  pinnedCellClasses,
  pinnedCellStyle,
  rowHighlightClass,
  STICKY_CELL_DIVIDER,
  type ColumnAlign,
  type PinMeta,
} from "./TableInternals"
import type { RowAction, RowCopyTargets, SelectionModifiers } from "./types"

/** Sort indicator: a direction arrow on the active column only. */
export function SortIcon({ sorted }: { sorted: false | "asc" | "desc" }) {
  if (sorted === "asc") {
    return <ArrowUp className="size-3.5" aria-hidden />
  }
  if (sorted === "desc") {
    return <ArrowDown className="size-3.5" aria-hidden />
  }
  return null
}

export function HeaderCell<T>({
  header,
  align,
  pin,
  scrolledX,
  canScrollRight,
  platterHeader,
}: {
  header: Header<T, unknown>
  align: ColumnAlign
  pin: PinMeta | undefined
  scrolledX: boolean
  canScrollRight: boolean
  platterHeader: boolean
}) {
  const rightAligned = align === "right"
  const canSort = header.column.getCanSort()
  const sorted = header.column.getIsSorted()
  const canResize = header.column.getCanResize()
  const isResizing = header.column.getIsResizing()
  const label = header.isPlaceholder
    ? null
    : flexRender(header.column.columnDef.header, header.getContext())

  const labelContent = (
    <span className="block min-w-0 max-w-full truncate whitespace-nowrap">
      {label}
    </span>
  )
  const content = canSort ? (
    <button
      type="button"
      onClick={header.column.getToggleSortingHandler()}
      className={cn(
        "flex min-w-0 max-w-full items-center gap-1 overflow-hidden whitespace-nowrap rounded-sm hover:text-coval-text-muted focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-coval-border",
        rightAligned && "flex-row-reverse",
      )}
    >
      {labelContent}
      <span className="shrink-0">
        <SortIcon sorted={sorted} />
      </span>
    </button>
  ) : (
    labelContent
  )

  // A pinned cell paints outside its own box — the gutter cover beyond its edge
  // and the edge fade against its inner edge — so `overflow-hidden` on the cell
  // would clip them. It clips its content in an inner wrapper and stays
  // `overflow-visible` instead. Mirrors the data cell.
  const clipsInWrapper = pin != null

  return (
    <div
      role="columnheader"
      aria-sort={
        sorted === "asc"
          ? "ascending"
          : sorted === "desc"
            ? "descending"
            : canSort
              ? "none"
              : undefined
      }
      style={pinnedCellStyle(pin)}
      className={cn(
        // `min-w-0` keeps a wide header label inside its own track rather than
        // letting the grid item's automatic minimum size push it across the
        // next column (see the matching note on the data cell).
        "group/hcell relative flex min-w-0 items-center whitespace-nowrap px-3 py-2 font-semibold text-coval-text",
        // Every column header stays on one line and clips inside its own track.
        // The label wrapper provides the ellipsis while this outer cell keeps
        // pinned-edge chrome available outside the clipping wrapper.
        clipsInWrapper ? "overflow-visible" : "overflow-hidden",
        alignClasses(align),
        pin &&
          pinnedCellClasses(pin, { scrolledX, canScrollRight, platterHeader }),
      )}
    >
      {clipsInWrapper ? (
        <div
          className={cn(
            "flex w-full min-w-0 items-center overflow-hidden",
            align === "right" && "justify-end",
            align === "center" && "justify-center",
          )}
        >
          {content}
        </div>
      ) : (
        content
      )}
      {canResize && (
        // Sits on the column's right edge; revealed on header-cell hover or
        // while dragging, in the tone every resize affordance uses (see
        // ResizeGrip). Kept to the header cell's height rather than the grip's
        // own, since it divides columns rather than panes. `onClick` stop keeps
        // a drag from toggling the sort.
        <div
          role="separator"
          aria-orientation="vertical"
          aria-hidden="true"
          onMouseDown={header.getResizeHandler()}
          onTouchStart={header.getResizeHandler()}
          onClick={(event) => event.stopPropagation()}
          className={cn(
            "absolute right-0 top-1 bottom-1 w-[5px] cursor-col-resize touch-none select-none rounded-[2.5px]",
            "bg-coval-border-strong",
            "opacity-0 transition-opacity group-hover/hcell:opacity-100",
            isResizing && "opacity-100",
          )}
        />
      )}
    </div>
  )
}

/**
 * Select-all header cell. Tri-state: checked when every loaded row is selected,
 * indeterminate when some are, unchecked when none. Toggling selects or clears
 * the loaded rows. `disabled` covers the case where part of the scope cannot be
 * toggled, so the control would do less than it claims.
 *
 * `showControl={false}` keeps the cell but drops the checkbox, for a table that
 * offers no select-all: the rows still reserve the selection track, so a header
 * without the cell would sit one column left of the data it labels.
 */
export function SelectAllHeaderCell({
  allSelected,
  someSelected,
  disabled,
  showControl = true,
  onToggle,
  sticky,
  divider,
  edgeGutterPx,
  platterHeader,
}: {
  allSelected: boolean
  someSelected: boolean
  disabled?: boolean
  showControl?: boolean
  onToggle: () => void
  sticky: boolean
  divider: boolean
  edgeGutterPx: number
  platterHeader: boolean
}) {
  return (
    <div
      role="columnheader"
      style={
        sticky
          ? { position: "sticky", left: edgeGutterPx, zIndex: 1 }
          : undefined
      }
      className={cn(
        "flex items-center justify-center px-3 py-2",
        // Carry the header's bottom divider under the opaque sticky cell (see
        // STICKY_CELL_DIVIDER) — except under a platter table's floating
        // header, which has neither that surface nor that divider.
        // `self-stretch` keeps the cell the full height of the header row — in
        // a row that centers its cells, an unstretched cell ends short of the
        // row's own bottom border and its divider lands above it as a second
        // line.
        sticky &&
          (platterHeader
            ? "self-stretch bg-surface-contrast"
            : cn("self-stretch bg-surface-default", STICKY_CELL_DIVIDER)),
        // Fade the data columns into the selection column as they scroll under
        // it.
        divider && edgeFadeClasses("left"),
        // Outermost left sticky cell — cover the gutter strip to its left.
        sticky && gutterCoverClasses("left", { divider: !platterHeader }),
      )}
    >
      {showControl ? (
        <Checkbox
          aria-label="Select all rows"
          checked={allSelected ? true : someSelected ? "indeterminate" : false}
          disabled={disabled}
          onCheckedChange={onToggle}
        />
      ) : null}
    </div>
  )
}

/**
 * Row selection cell. The checkbox is revealed on row hover or keyboard focus
 * and stays visible while the row is selected — no persistent select-mode
 * toggle — unless `alwaysVisible` keeps it shown at rest, for a table whose
 * whole purpose is picking rows. A cell that owns a checkbox opts out of row
 * navigation (see `shouldNavigateRow`), so a click landing in the padding
 * beside it does nothing instead of navigating away and dropping the
 * selection. Two cases keep the cell in the row's click target instead: a
 * non-selectable row, which renders no checkbox, and a table whose row click
 * is itself the selection action (`rowClickSelects`), where a padding click
 * toggles rather than costing the selection.
 *
 * `onToggle` receives the activating event's modifier state so the table can
 * treat a shift-click as "extend from the last row I picked" rather than a
 * plain toggle. It hangs off the checkbox's `onClick` rather than
 * `onCheckedChange`, which reports the next checked value but not the event
 * that produced it; the checkbox is fully controlled by `selected`, so the
 * click is the only toggle path either way, and keyboard activation (Space)
 * dispatches a click too, carrying its own shift state.
 */
export function SelectCell({
  selectable,
  disabled,
  selected,
  active,
  warning,
  onToggle,
  sticky,
  divider,
  hoverable,
  edgeGutterPx,
  alwaysVisible,
  rowClickSelects,
  cardRows,
}: {
  selectable: boolean
  disabled: boolean
  selected: boolean
  /** Keyboard/collection cursor, which highlights without being a pick. */
  active: boolean
  warning: boolean
  onToggle: (modifiers: SelectionModifiers) => void
  sticky: boolean
  divider: boolean
  hoverable: boolean
  edgeGutterPx: number
  alwaysVisible: boolean
  rowClickSelects: boolean
  cardRows: boolean
}) {
  return (
    <div
      role="cell"
      data-prevent-row-click={
        selectable && !rowClickSelects ? "true" : undefined
      }
      style={
        sticky
          ? { position: "sticky", left: edgeGutterPx, zIndex: 1 }
          : undefined
      }
      className={cn(
        "flex items-center justify-center px-3 py-2",
        // When left-pinned, the cell needs an opaque background that fills the
        // row height (`self-stretch`) and tracks the row state so scrolling
        // center cells don't bleed through behind it. The hover background is
        // gated on `hoverable` to match the row (and the pinned data cells) —
        // otherwise a row that doesn't tint under the pointer would light up
        // only this cell. Carry the row divider under this opaque sticky cell
        // (see STICKY_CELL_DIVIDER).
        sticky && cn("self-stretch bg-surface-default", STICKY_CELL_DIVIDER),
        // This cell follows the row it belongs to, or the selection column
        // tints a different shade than the rest of the row.
        sticky && rowHighlightClass({ selected, active, cardRows }),
        sticky &&
          warning &&
          "bg-coval-warning-surface text-coval-warning-on-surface",
        // Hover matches the row's.
        sticky && hoverable && "group-hover:bg-surface-contrast",
        sticky &&
          warning &&
          hoverable &&
          "group-hover:bg-coval-warning-surface",
        // Fade the data columns into the selection column as they scroll under
        // it.
        divider && edgeFadeClasses("left"),
        // Outermost left sticky cell — cover the gutter strip to its left.
        sticky && gutterCoverClasses("left"),
      )}
    >
      {/* A non-selectable row keeps the cell (for grid alignment) but renders no
          checkbox. A row that is merely blocked keeps a disabled one, so the
          reason it cannot be picked reads as a state rather than an absence —
          and it stays visible at rest, since there is nothing to reveal. */}
      {selectable && (
        <span
          className={cn(
            "flex transition-opacity",
            // Checkbox hidden until the row is hovered or focused (the row is
            // tabbable when clickable, so reveal on `group-focus-within` too).
            // `pointer-events` track opacity so a click on the invisible checkbox
            // can't silently toggle. The opacity lives on this inner wrapper, not
            // the cell, so the pinned cell's background stays opaque.
            !selected &&
              !alwaysVisible &&
              !disabled &&
              "pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100 focus-within:pointer-events-auto focus-within:opacity-100",
          )}
        >
          <Checkbox
            aria-label="Select row"
            checked={selected}
            disabled={disabled}
            // A shift-click would otherwise extend the document's text
            // selection across the rows it spans; suppressing the default
            // mousedown drops that without touching the click itself.
            onMouseDown={(event) => {
              if (event.shiftKey) {
                event.preventDefault()
              }
            }}
            onClick={(event) => onToggle({ shiftKey: event.shiftKey })}
          />
        </span>
      )}
    </div>
  )
}

/**
 * Expand/collapse control for a nestable row. A `button`, so clicking
 * it toggles expansion without triggering row navigation (see
 * `shouldNavigateRow`); the chevron rotates to point down when expanded.
 */
export function ExpandToggle({
  expanded,
  onToggle,
  className,
}: {
  expanded: boolean
  onToggle: (event: React.MouseEvent) => void
  className?: string
}) {
  return (
    <button
      type="button"
      aria-label={expanded ? "Collapse row" : "Expand row"}
      aria-expanded={expanded}
      onClick={onToggle}
      className={cn(
        "flex size-4 shrink-0 items-center justify-center rounded-sm text-coval-text-muted hover:text-coval-text focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-coval-border",
        className,
      )}
    >
      <ChevronRight
        aria-hidden
        className={cn("size-3.5 transition-transform", expanded && "rotate-90")}
      />
    </button>
  )
}

/** Header cell above the trailing row-actions column. */
export function ActionsHeaderCell({
  children,
  sticky,
  edgeFade,
  edgeGutterPx,
  platterHeader,
}: {
  children?: React.ReactNode
  sticky: boolean
  edgeFade: boolean
  edgeGutterPx: number
  platterHeader: boolean
}) {
  return (
    <div
      role="columnheader"
      style={
        sticky
          ? { position: "sticky", right: edgeGutterPx, zIndex: 1 }
          : undefined
      }
      // When pinned (table overflows), carry the header's bottom divider under
      // this opaque sticky cell (see STICKY_CELL_DIVIDER) and, as the outermost
      // right sticky cell, cover the gutter strip to its right.
      className={cn(
        "flex items-center justify-center px-1 py-1",
        sticky &&
          cn(
            // A platter table's floating header takes the contrast surface it
            // sits on and no divider, like the header row itself.
            platterHeader
              ? "self-stretch bg-surface-contrast"
              : cn("self-stretch bg-surface-default", STICKY_CELL_DIVIDER),
            // Fade content into the actions column's inner edge when it is the
            // innermost right pin.
            edgeFade && edgeFadeClasses("right"),
            // The platter header has no divider, so its cover carries none
            // either.
            gutterCoverClasses("right", { divider: !platterHeader }),
          ),
      )}
    >
      {children}
    </div>
  )
}

function resolveRowActions<T>(
  row: T,
  actions: RowAction<T>[],
): ResourceMenuAction[] {
  return actions.map((action) => ({
    ...action,
    disabled:
      typeof action.disabled === "function"
        ? action.disabled(row)
        : action.disabled,
    onSelect: () => action.onSelect(row),
  }))
}

/**
 * Opens a row's actions where the pointer is, on right-click anywhere in the
 * row. `children` is the row element itself: the trigger takes `asChild` so the
 * row keeps its own place in the grid rather than gaining a wrapper element.
 */
export function RowContextMenu<T>({
  row,
  actions,
  copy,
  children,
}: {
  row: T
  actions: RowAction<T>[]
  copy: RowCopyTargets
  children: React.ReactElement
}) {
  const { copied, copy: copyToClipboard } = useCopyToClipboardFeedback()
  if (actions.length === 0 && !copy.id && !copy.url) {
    return <>{children}</>
  }
  const menuActions = resolveRowActions(row, actions)
  const copyTargets = normalizeResourceCopyTargets(copy)
  return (
    <ContextMenu>
      <Tooltip content="Copied!" forceOpen={copied} openOnInteraction={false}>
        <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      </Tooltip>
      <ContextMenuContent>
        <ResourceMenuItems
          actions={menuActions}
          copy={copyTargets}
          onCopy={(value) => {
            void copyToClipboard(value)
          }}
          item={ContextMenuItem}
          separator={ContextMenuSeparator}
        />
      </ContextMenuContent>
    </ContextMenu>
  )
}

/**
 * Trailing row-actions cell. A hover/focus-revealed `…` opens a
 * DropdownMenu of the row's actions and stays visible while the menu is open.
 * Right-pinned so it remains reachable during horizontal scroll; the trigger is
 * a `button` and the menu items are portaled, so neither triggers row
 * navigation (see `shouldNavigateRow`).
 */
export function ActionCell<T>({
  row,
  actions,
  copy,
  selected,
  active,
  warning,
  hoverable,
  sticky,
  edgeFade,
  edgeGutterPx,
  cardRows,
}: {
  row: T
  actions: RowAction<T>[]
  copy: RowCopyTargets
  selected: boolean
  /** Keyboard/collection cursor, which highlights without being a pick. */
  active: boolean
  warning: boolean
  hoverable: boolean
  sticky: boolean
  edgeFade: boolean
  edgeGutterPx: number
  // Platter rows highlight with a different surface than table rows do, and a
  // pinned cell paints its own background, so it has to follow the row it sits in.
  cardRows: boolean
}) {
  const { copied, copy: copyToClipboard } = useCopyToClipboardFeedback()
  const menuActions = resolveRowActions(row, actions)
  const copyTargets = normalizeResourceCopyTargets(copy)
  return (
    <div
      role="cell"
      style={
        sticky
          ? { position: "sticky", right: edgeGutterPx, zIndex: 1 }
          : undefined
      }
      className={cn(
        "flex items-center justify-center px-1",
        // When pinned (table overflows), carry the row divider under this opaque
        // sticky cell (see STICKY_CELL_DIVIDER) and cover the right gutter strip
        // as the outermost right sticky cell.
        sticky &&
          cn(
            "self-stretch bg-surface-default",
            STICKY_CELL_DIVIDER,
            rowHighlightClass({ selected, active, cardRows }),
            warning && "bg-coval-warning-surface text-coval-warning-on-surface",
            // Hover matches the row's.
            hoverable && "group-hover:bg-surface-contrast",
            warning && hoverable && "group-hover:bg-coval-warning-surface",
            // Fade content into the actions column's inner edge when it is the
            // innermost right pin.
            edgeFade && edgeFadeClasses("right"),
            gutterCoverClasses("right"),
          ),
      )}
    >
      {(menuActions.length > 0 ||
        Boolean(copyTargets.id) ||
        Boolean(copyTargets.url)) && (
        <DropdownMenu>
          <Tooltip
            content={copied ? "Copied!" : "Row actions"}
            forceOpen={copied}
          >
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Row actions"
                className={cn(
                  "flex size-7 items-center justify-center rounded-md text-coval-text-muted transition-opacity hover:bg-surface-default-hover hover:text-coval-text focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-coval-border",
                  warning &&
                    "text-coval-warning-on-surface hover:text-coval-warning-on-surface",
                  // Revealed on row hover / focus and while the menu is open;
                  // pointer-events track opacity so the hidden trigger can't be
                  // clicked by accident.
                  "pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100 focus-visible:pointer-events-auto focus-visible:opacity-100 aria-expanded:pointer-events-auto aria-expanded:opacity-100",
                )}
              >
                <MoreHorizontal className="size-4" aria-hidden />
              </button>
            </DropdownMenuTrigger>
          </Tooltip>
          <DropdownMenuContent align="end">
            <ResourceMenuItems
              actions={menuActions}
              copy={copyTargets}
              onCopy={(value) => {
                void copyToClipboard(value)
              }}
              item={DropdownMenuItem}
              separator={DropdownMenuSeparator}
            />
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
}
